import { fetchProcessedBankStatements, fetchProcessedCCStatements, fetchCheckImages } from '../api/cases';
import { CaseFile } from '../types';

// ── CSV helpers ───────────────────────────────────────────────────────────────

function escapeCSV(value: unknown): string {
  if (value == null) return '';
  const s = String(value);
  // Wrap in quotes if the value contains commas, quotes, or newlines
  if (s.includes(',') || s.includes('"') || s.includes('\n') || s.includes('\r')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

const CSV_HEADERS = [
  'date',
  'description',
  'amount',
  'transaction_type',
  'data_type',
  'account_number',
  'account_last4',
  'institution',
  'statement_date',
  'statement_id',
  'file_name',
  'file_id',
];

interface TxRow {
  date: string;
  description: string;
  amount: string | number;
  transaction_type: string;
  data_type: string;
  account_number: string;
  account_last4?: string;
  institution: string;
  statement_date: string;
  statement_id?: string;
  file_name: string;
  file_id: string;
}

function rowToCSV(row: TxRow): string {
  return CSV_HEADERS.map((h) => escapeCSV((row as Record<string, unknown>)[h])).join(',');
}

function triggerDownload(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

// ── Main export function ──────────────────────────────────────────────────────

/**
 * Fetches all extracted transaction data for a case and downloads a CSV.
 *
 * @param caseId       Case identifier
 * @param token        Auth token
 * @param allFiles     All CaseFile records (used to resolve filenames and detect check-image files)
 * @param selectedIds  Optional set of file_ids to restrict the export; if empty/omitted, exports everything
 */
export async function downloadTransactionsCSV(
  caseId: string,
  token: string,
  allFiles: CaseFile[],
  selectedIds?: Set<string>,
): Promise<void> {
  const filterByFile = selectedIds && selectedIds.size > 0 ? selectedIds : null;

  const fileMap = new Map(allFiles.map((f) => [f.file_id, f]));
  const rows: TxRow[] = [];

  // ── 1. Bank statement transactions ─────────────────────────────────────────
  try {
    const bankResp = await fetchProcessedBankStatements(caseId, token);
    const bankStatements: any[] = bankResp.data?.statements ?? [];

    for (const stmt of bankStatements) {
      if (filterByFile && !filterByFile.has(stmt.file_id)) continue;
      const fileName = fileMap.get(stmt.file_id)?.filename ?? stmt.file_id;

      for (const tx of (stmt.transactions ?? [])) {
        rows.push({
          date: tx.date ?? '',
          description: tx.description ?? '',
          amount: tx.amount ?? '',
          transaction_type: tx.transaction_type ?? '',
          data_type: 'bank_statement',
          account_number: stmt.account_number ?? '',
          account_last4: (() => {
            try { const raw = String(stmt.account_number || ''); const digits = raw.replace(/\D/g, ''); return digits.length >= 4 ? digits.slice(-4) : digits; } catch(e){ return '' }
          })(),
          institution: stmt.bank_name ?? '',
          statement_date: stmt.statement_date ?? '',
          statement_id: stmt.statement_id ?? '',
          file_name: fileName,
          file_id: stmt.file_id,
        });
      }
    }
  } catch (err) {
    console.warn('Could not fetch bank statements for export:', err);
  }

  // ── 2. Credit card statement transactions ───────────────────────────────────
  try {
    const ccResp = await fetchProcessedCCStatements(caseId, token);
    const ccStatements: any[] = ccResp.data?.statements ?? [];

    for (const stmt of ccStatements) {
      if (filterByFile && !filterByFile.has(stmt.file_id)) continue;
      const fileName = fileMap.get(stmt.file_id)?.filename ?? stmt.file_id;

      for (const tx of (stmt.transactions ?? [])) {
        rows.push({
          date: tx.date ?? '',
          description: tx.description ?? '',
          amount: tx.amount ?? '',
          transaction_type: tx.transaction_type ?? '',
          data_type: 'credit_card',
          account_number: stmt.account_number ?? '',
          account_last4: (() => {
            try { const raw = String(stmt.account_number || ''); const digits = raw.replace(/\D/g, ''); return digits.length >= 4 ? digits.slice(-4) : digits; } catch(e){ return '' }
          })(),
          institution: stmt.card_issuer ?? '',
          statement_date: stmt.statement_date ?? '',
          statement_id: stmt.statement_id ?? '',
          file_name: fileName,
          file_id: stmt.file_id,
        });
      }
    }
  } catch (err) {
    console.warn('Could not fetch CC statements for export:', err);
  }

  // ── 3. Check images ─────────────────────────────────────────────────────────
  // Only fetch for files that are typed as check_image to avoid unnecessary calls
  const checkFiles = allFiles.filter(
    (f) =>
      f.file_type === 'check_image' &&
      (!filterByFile || filterByFile.has(f.file_id)),
  );

  for (const file of checkFiles) {
    try {
      const checkResp = await fetchCheckImages(caseId, file.file_id, token);
      const checks: any[] = checkResp.data?.check_images ?? [];

      for (const check of checks) {
        rows.push({
          date: check.date ?? '',
          description: `Check — ${check.payee ?? 'Unknown payee'}`,
          amount: check.amount ?? '',
          transaction_type: 'check',
          data_type: 'check_image',
          account_number: check.account_number ?? '',
          account_last4: (() => {
            try { const raw = String(check.account_number || ''); const digits = raw.replace(/\D/g, ''); return digits.length >= 4 ? digits.slice(-4) : digits; } catch(e){ return '' }
          })(),
          institution: check.bank_name ?? '',
          statement_date: '',
          statement_id: '',
          file_name: file.filename,
          file_id: file.file_id,
        });
      }
    } catch {
      // Silently skip files that have no check images
    }
  }

  // ── Sort by date (ISO strings sort lexicographically) ──────────────────────
  rows.sort((a, b) => (a.date || '').localeCompare(b.date || ''));

  if (rows.length === 0) {
    throw new Error('No extracted transactions found for the selected files.');
  }

  // ── Build CSV string ───────────────────────────────────────────────────────
  const csvLines = [CSV_HEADERS.join(','), ...rows.map(rowToCSV)];
  const csvContent = csvLines.join('\n');

  const date = new Date().toISOString().split('T')[0];
  const suffix = filterByFile ? `_${filterByFile.size}files` : '_all';
  triggerDownload(csvContent, `transactions_${caseId}${suffix}_${date}.csv`);
}
